import React, { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../services/api";

const DEFAULT_CENTER = [18.1514, 74.5815]; // Baramati, Maharashtra

const createDriverIcon = (status) => {
  const color = status === "FREE" ? "%2316a34a" : "%23dc2626";
  return new L.Icon({
    iconUrl: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="36px" height="36px"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -32],
  });
};

// Pans/zooms the map whenever `target` changes — used by the search bar.
function MapFlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 13, { duration: 1.2 });
  }, [target, map]);
  return null;
}

const formatLastUpdated = (iso) => {
  if (!iso) return "Never";
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  return new Date(iso).toLocaleString();
};

export default function AllDriversMap() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [flyToTarget, setFlyToTarget] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchAllDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/map/all-drivers");
      if (res.data.success) {
        setDrivers(res.data.drivers || []);
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load online drivers" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllDrivers();
  }, [fetchAllDrivers]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    try {
      setSearching(true);
      const res = await api.get("/map/search-location", { params: { query: searchQuery.trim() } });
      if (res.data.success) {
        setFlyToTarget([res.data.latitude, res.data.longitude]);
      } else {
        setMessage({ type: "error", text: "Location not found" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Search failed" });
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>All Online Drivers</h1>
          <p style={styles.subtitle}>
            Real-time fleet tracking ({drivers.length} drivers currently online)
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchAllDrivers}>
          {loading ? "Refreshing..." : "Refresh Drivers"}
        </button>
      </div>

      {/* SEARCH BAR */}
      <form onSubmit={handleSearch} style={styles.searchBar}>
        <input
          type="text"
          placeholder="Search a place to focus the map (e.g. Pune, Baramati)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <button type="submit" style={styles.searchBtn} disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {message && (
        <div
          style={{
            ...styles.banner,
            background: message.type === "success" ? "#dcfce7" : "#fee2e2",
            color: message.type === "success" ? "#166534" : "#991b1b",
          }}
        >
          {message.text}
        </div>
      )}

      {/* MAP WRAPPER */}
      <div style={styles.mapWrapper}>
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={12}
          style={{ width: "100%", height: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapFlyTo target={flyToTarget} />

          {drivers.map((driver) => {
            if (!driver.coordinates?.latitude || !driver.coordinates?.longitude) return null;

            return (
              <Marker
                key={driver._id}
                position={[driver.coordinates.latitude, driver.coordinates.longitude]}
                icon={createDriverIcon(driver.status)}
              >
                <Popup>
                  <div style={styles.infoWindow}>
                    <h4 style={styles.infoName}>{driver.name}</h4>
                    <p style={styles.infoText}>📞 {driver.phone}</p>
                    <p style={styles.infoText}>🚗 Vehicle: {driver.vehicleType}</p>

                    <div style={styles.badgeContainer}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          background: driver.status === "FREE" ? "#dcfce7" : "#fee2e2",
                          color: driver.status === "FREE" ? "#15803d" : "#b91c1c",
                        }}
                      >
                        {driver.status}
                      </span>
                      <span
                        style={{
                          ...styles.statusBadge,
                          marginLeft: "6px",
                          background: driver.isOnline ? "#dbeafe" : "#f1f5f9",
                          color: driver.isOnline ? "#1d4ed8" : "#64748b",
                        }}
                      >
                        {driver.isOnline ? "Online" : "Offline"}
                      </span>
                    </div>

                    <p style={styles.infoText}>🕒 Updated: {formatLastUpdated(driver.lastLocationUpdate)}</p>

                    {/* ACTIVE ASSIGNMENT DETAILS */}
                    {driver.status === "BUSY" && driver.currentBooking && (
                      <div style={styles.bookingBox}>
                        <div style={styles.bookingTitle}>Active Assignment</div>
                        <div><strong>Booking ID:</strong> {driver.currentBooking.bookingId}</div>
                        <div><strong>Status:</strong> {driver.currentBooking.bookingStatus}</div>
                        <div><strong>Pickup:</strong> {driver.currentBooking.pickupLocation}</div>
                        <div><strong>Drop:</strong> {driver.currentBooking.dropLocation}</div>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

/* ================= RESPONSIVE STYLES ================= */
const styles = {
  page: {
    padding: "clamp(12px, 3vw, 24px)",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "12px",
  },
  title: {
    margin: 0,
    fontSize: "clamp(20px, 4vw, 26px)",
    fontWeight: "800",
    color: "#0f172a",
  },
  subtitle: {
    margin: "4px 0 0 0",
    color: "#64748b",
    fontSize: "clamp(12px, 2.5vw, 14px)",
  },
  refreshBtn: {
    background: "#2563eb",
    color: "#ffffff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    width: "auto",
    maxWidth: "100%",
  },
  searchBar: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  searchInput: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    outline: "none",
  },
  searchBtn: {
    background: "#16a34a",
    color: "#ffffff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
  },
  banner: {
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  mapWrapper: {
    height: "calc(100vh - 220px)",
    minHeight: "400px",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  },
  infoWindow: {
    padding: "4px",
    maxWidth: "220px",
    fontFamily: "'Inter', sans-serif",
  },
  infoName: {
    margin: "0 0 6px 0",
    fontSize: "14px",
    fontWeight: "700",
    color: "#0f172a",
  },
  infoText: {
    margin: "2px 0",
    fontSize: "12px",
    color: "#334155",
  },
  badgeContainer: {
    marginTop: "6px",
    marginBottom: "8px",
  },
  statusBadge: {
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "10px",
    fontWeight: "800",
  },
  bookingBox: {
    marginTop: "8px",
    padding: "8px",
    background: "#f1f5f9",
    borderRadius: "6px",
    fontSize: "11px",
    color: "#1e293b",
    lineHeight: "1.4",
    wordBreak: "break-word",
  },
  bookingTitle: {
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "4px",
    borderBottom: "1px solid #cbd5e1",
    paddingBottom: "2px",
  },
};